// ─── VOUCHER (Voucher Produk + Voucher Ongkir) — helper MURNI, tanpa I/O ────
//
// Rumus di berkas ini adalah CERMIN TS dari fungsi SQL yang menjadi OTORITAS:
//   • `voucher_amount(mode, value, max, base)`      ↔ `voucherAmount`
//   • `voucher_allocate_lines(total, lines)`        ↔ `allocateVoucher`
//   • `voucher_unit_price_after(...)`               ↔ `unitPriceAfterVoucher`
// Daftar kasus paritasnya ada di `src/lib/__tests__/voucher.test.ts` dan
// diulang PERSIS di suite SQL offline (`supabase/tests/voucher_engine_*.sql`).
// Server SELALU menghitung ulang dan menulis angkanya sendiri saat checkout /
// saat nota ongkir terbit; angka dari berkas ini hanya untuk pratinjau layar.
//
// Keputusan pemilik (2026-09-09):
//   1. Urutan harga: HARGA JUAL UMUM − voucher, BARU diskon level/promo
//      (tier) — `unitPriceAfterVoucher`. Pada tier persen, hemat pembeli lebih
//      kecil dari nilai voucher (diskon level ikut mengecil); itu disengaja.
//   2. Maks SATU voucher produk per checkout & SATU voucher ongkir per paket.
//   3. Setelan `allowWithPromo` hanya mengatur baris berharga PROMO kampanye:
//      mati → baris promo dikeluarkan dari basis voucher, baris lain tetap dapat.
//   4. Cakupan tipe produk (PO/Ready) menyaring LEBIH DULU (idiom promo/rail).
//
// Aritmetika memakai BigInt supaya pembulatan TS identik dengan `numeric`
// Postgres: alokasi largest-remainder (Σ alokasi = total PERSIS), dan
// pembulatan "setengah ke atas" untuk harga satuan.

import type { PriceCut, PriceTier } from "@/lib/pricing";
import { isLevelCut } from "@/lib/promo";
import { DISKON_SLOTS } from "@/lib/reseller-groups";

export type VoucherKind = "product" | "ongkir";
export type VoucherDiscountMode = "persen" | "nominal";
/** Status tersimpan. Kedaluwarsa & Habis DITURUNKAN saat baca (`voucherEffectiveStatus`). */
export type VoucherStatus = "draft" | "active" | "inactive";
export type VoucherEffectiveStatus =
  | "Draft"
  | "Aktif"
  | "Terjadwal"
  | "Nonaktif"
  | "Kedaluwarsa"
  | "Habis";
export type VoucherTargetKind = "product" | "category" | "brand";
export type VoucherTargetType = VoucherTargetKind | "all";
export type VoucherProductTypeScope = "preorder" | "ready_stock" | "all";
export type VoucherExpeditionScope = "all" | "selected";

export type VoucherTarget = {
  kind: VoucherTargetKind;
  productId: string | null;
  /** Snapshot nama (kategori & merek dicocokkan BY NAMA, cermin promo). */
  name: string;
};

export type VoucherExpedition = {
  expeditionId: string | null;
  /** Snapshot nama ekspedisi toko; pencocokan by nama (lower+trim). */
  name: string;
};

/**
 * Syarat potongan yang DISNAPSHOT ke `voucher_usages.terms` saat dipakai —
 * mengedit voucher tidak pernah mengubah transaksi yang sudah terjadi.
 */
export type VoucherTerms = {
  mode: VoucherDiscountMode;
  value: number;
  /** Plafon potongan (OPSIONAL untuk persen sejak mig 5660 — null = tanpa plafon; selalu null untuk nominal). */
  maxDiscount: number | null;
  minPurchase: number;
  /** Batas nilai pembelian yang mendapat potongan (produk saja). */
  maxPurchaseBase: number | null;
};

export type Voucher = VoucherTerms & {
  id: string;
  storeId: string | null;
  kind: VoucherKind;
  name: string;
  description: string | null;
  status: VoucherStatus;
  startsAt: string | null;
  endsAt: string | null;
  quotaTotal: number | null;
  perCustomerLimit: number | null;
  /** Slot level yang berhak; 0 = tanpa level; kosong = semua pembeli. */
  levelSlots: number[];
  productTypeScope: VoucherProductTypeScope;
  targetType: VoucherTargetType;
  targets: VoucherTarget[];
  expeditionScope: VoucherExpeditionScope;
  expeditions: VoucherExpedition[];
  allowWithPromo: boolean;
  /**
   * Kode voucher (mig 6990), ternormalisasi A-Z0-9. `null` = "Tampil di daftar".
   * Hanya layar ADMIN yang menerimanya — RPC pembeli membuang kunci ini, jadi di
   * sisi pembeli bacalah `requiresCode`.
   */
  code: string | null;
  /** Voucher ber-kode: tersembunyi dari daftar pembeli, kodenya wajib diketik di setiap checkout. */
  requiresCode: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

/**
 * Slot level yang mungkin dimiliki pembeli; 0 = tanpa level. Cermin CHECK
 * `vouchers.level_slots` (mig 5610).
 *
 * DITURUNKAN dari `MAX_GROUP_SLOT`, bukan dieja — plafonnya milik
 * `reseller-groups.ts` dan pernah bergerak (4 → 8, mig 5600). Selama angkanya
 * dieja di sini, berkas ini bisa (dan sempat) menyimpan DUA plafon berbeda
 * sekaligus: konstanta ini sudah 8 sementara saringan baca/tulis di bawah masih
 * 4, sehingga voucher untuk grup slot 5+ tersimpan sebagai "semua pembeli".
 */
export const VOUCHER_LEVEL_SLOTS = [0, ...DISKON_SLOTS] as const;

// ─── Utilitas kecil ─────────────────────────────────────────────────────────

function normName(raw: string | null | undefined): string {
  return (raw ?? "").trim().toLowerCase();
}

function parseTime(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Angka defensif dari PostgREST (numeric bisa datang sebagai string). */
function num(raw: unknown, fallback = 0): number {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : fallback;
  if (typeof raw === "string") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function numOrNull(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = num(raw, Number.NaN);
  return Number.isFinite(n) ? n : null;
}

const MICRO = BigInt(1_000_000);
const HUNDRED_MICRO = BigInt(100_000_000);

/** Skala ke sepersejuta sebagai BigInt (persen 12,5 → 12500000). */
function micro(value: number): bigint {
  if (!Number.isFinite(value)) return BigInt(0);
  return BigInt(Math.round(value * 1_000_000));
}

function toBig(value: number): bigint {
  if (!Number.isFinite(value)) return BigInt(0);
  return BigInt(Math.trunc(value));
}

/** Pembulatan setengah-ke-atas untuk pecahan num/den (den > 0), num boleh negatif → 0. */
function roundHalfUpRatio(numer: bigint, den: bigint): number {
  if (den <= BigInt(0)) return 0;
  if (numer <= BigInt(0)) return 0;
  const twice = BigInt(2);
  return Number((twice * numer + den) / (twice * den));
}

// ─── Rumus potongan ─────────────────────────────────────────────────────────

/**
 * Nilai potongan untuk satu basis (produk: Σ harga jual umum baris layak;
 * ongkir: ongkir bruto). Cermin SQL `voucher_amount`:
 *   persen  → min(floor(base × pct / 100), maxDiscount)
 *   nominal → min(value, base)
 * Selalu bilangan bulat rupiah ≥ 0; basis ≤ 0 → 0.
 */
export function voucherAmount(
  terms: Pick<VoucherTerms, "mode" | "value" | "maxDiscount">,
  base: number
): number {
  const b = Math.floor(Number.isFinite(base) ? base : 0);
  if (b <= 0) return 0;
  const value = Number.isFinite(terms.value) ? Math.max(0, terms.value) : 0;
  if (terms.mode === "nominal") {
    return Math.max(0, Math.min(Math.floor(value), b));
  }
  // floor(base × pct / 100) dengan pct berskala sepersejuta → exact di BigInt.
  const raw = Number((toBig(b) * micro(value)) / HUNDRED_MICRO);
  const cap =
    terms.maxDiscount !== null && Number.isFinite(terms.maxDiscount)
      ? Math.max(0, Math.floor(terms.maxDiscount))
      : Number.POSITIVE_INFINITY;
  return Math.max(0, Math.min(raw, cap, b));
}

/** Basis yang berhak potongan: `min(base, maxPurchaseBase)` (produk saja). */
export function voucherDiscountableBase(
  terms: Pick<VoucherTerms, "maxPurchaseBase">,
  base: number
): number {
  const b = Math.max(0, Math.floor(Number.isFinite(base) ? base : 0));
  if (terms.maxPurchaseBase === null || !Number.isFinite(terms.maxPurchaseBase)) return b;
  return Math.min(b, Math.max(0, Math.floor(terms.maxPurchaseBase)));
}

export type VoucherAllocationInput = {
  key: string;
  /** Basis baris = harga jual umum × qty (bulat, > 0). */
  base: number;
  qty: number;
};

export type VoucherAllocation = {
  key: string;
  /** Potongan untuk SELURUH baris (bulat). */
  alloc: number;
  /** Potongan per pcs = alloc / qty (boleh pecahan; tampilan dibulatkan). */
  perPcs: number;
};

/**
 * Sebar `total` ke baris pro-rata `base`, LARGEST REMAINDER: setiap baris
 * mendapat floor(total × base_i / Σbase), lalu sisanya (< n rupiah) dibagikan
 * satu-satu ke baris dengan sisa pembagian terbesar — seri dimenangkan baris
 * yang lebih dulu. Σ alokasi = total PERSIS, deterministik. Cermin SQL
 * `voucher_allocate_lines`; keduanya menghitung di bilangan bulat.
 */
export function allocateVoucher(
  total: number,
  lines: readonly VoucherAllocationInput[]
): VoucherAllocation[] {
  const valid = lines.filter((l) => Number.isFinite(l.base) && l.base > 0 && l.qty > 0);
  const t = Math.max(0, Math.floor(Number.isFinite(total) ? total : 0));
  if (!valid.length || t <= 0) {
    return valid.map((l) => ({ key: l.key, alloc: 0, perPcs: 0 }));
  }
  const sum = valid.reduce((s, l) => s + toBig(Math.floor(l.base)), BigInt(0));
  if (sum <= BigInt(0)) return valid.map((l) => ({ key: l.key, alloc: 0, perPcs: 0 }));

  const big = toBig(t);
  const floors: bigint[] = [];
  const rems: bigint[] = [];
  let given = BigInt(0);
  for (const l of valid) {
    const raw = big * toBig(Math.floor(l.base));
    const q = raw / sum;
    floors.push(q);
    rems.push(raw % sum);
    given += q;
  }
  let leftover = Number(big - given);
  const order = valid
    .map((_, i) => i)
    .sort((a, b) => (rems[a] === rems[b] ? a - b : rems[a] > rems[b] ? -1 : 1));
  const bonus = new Array<number>(valid.length).fill(0);
  for (const i of order) {
    if (leftover <= 0) break;
    bonus[i] = 1;
    leftover -= 1;
  }
  return valid.map((l, i) => {
    const alloc = Number(floors[i]) + bonus[i];
    return { key: l.key, alloc, perPcs: alloc / l.qty };
  });
}

/**
 * Harga satuan SETELAH voucher — tier (promo / override varian / diskon grup
 * yang memang dipilih `pricing.ts`) diterapkan pada `(umum − v)`:
 *   persen  L → round((N×qty − alloc) × (100 − L) / (100 × qty))
 *   nominal d → max(0, round((N×qty − alloc)/qty − d))
 *   tanpa     → round((N×qty − alloc)/qty)
 * Pembulatan setengah-ke-atas, cermin `round(numeric)` Postgres pada angka
 * positif; nilai negatif diklem ke 0. Cermin SQL `voucher_unit_price_after`.
 *
 * DUA TIER (mig voucher_dua_tier, keputusan pemilik 2026-09-16): kampanye
 * "Diskon harga coret" memotong dua kali — promo lalu level — jadi `tier2`
 * diterapkan pada HASIL BULAT langkah pertama, dibulatkan lagi:
 *   persen  L2 → max(0, round(p1 × (100 − L2) / 100))
 *   nominal d2 → max(0, round(p1 − d2))
 * Sama persis dengan rantai `applyPromo` → `applyGroupDiscount` di pricing.ts
 * (masing-masing membulatkan), dan dengan overload SQL 7-argumen. `tier2`
 * null ⇒ rumus lama persis (muatan klien lama tak berubah hasilnya).
 */
export function unitPriceAfterVoucher(
  normalPrice: number,
  qty: number,
  alloc: number,
  tier: PriceTier,
  tier2: PriceTier = null
): number {
  const n = toBig(Math.max(0, Math.floor(normalPrice)));
  const q = toBig(Math.max(1, Math.floor(qty)));
  const a = toBig(Math.max(0, Math.floor(alloc)));
  const netTimesQty = n * q - a; // (N − v) × qty, exact
  let p1: number;
  if (!tier) p1 = roundHalfUpRatio(netTimesQty, q);
  else if (tier.mode === "persen") {
    const lp = micro(Math.min(100, Math.max(0, tier.value)));
    p1 = roundHalfUpRatio(netTimesQty * (HUNDRED_MICRO - lp), HUNDRED_MICRO * q);
  } else {
    const dp = micro(Math.max(0, tier.value));
    p1 = roundHalfUpRatio(netTimesQty * MICRO - dp * q, MICRO * q);
  }
  if (!tier2) return p1;
  const p1Big = toBig(p1);
  if (tier2.mode === "persen") {
    const lp2 = micro(Math.min(100, Math.max(0, tier2.value)));
    return roundHalfUpRatio(p1Big * (HUNDRED_MICRO - lp2), HUNDRED_MICRO);
  }
  const dp2 = micro(Math.max(0, tier2.value));
  return roundHalfUpRatio(p1Big * MICRO - dp2, MICRO);
}

/** Potongan voucher satu baris (per-pcs × qty), dibulatkan untuk tampilan. */
export function voucherLineDiscount(
  perPcs: number | null | undefined,
  qty: number
): number {
  const v = Number(perPcs ?? 0);
  const q = Math.max(0, Math.floor(Number(qty) || 0));
  if (!Number.isFinite(v) || v <= 0 || q <= 0) return 0;
  return Math.round(v * q);
}

// ─── Status & kelayakan ─────────────────────────────────────────────────────

/** Aktif DAN `now` di dalam periode — pra-syarat tampil ke pembeli. */
export function isVoucherLive(
  voucher: Pick<Voucher, "status" | "startsAt" | "endsAt">,
  now: Date
): boolean {
  if (voucher.status !== "active") return false;
  const t = now.getTime();
  if (!Number.isFinite(t)) return false;
  const start = parseTime(voucher.startsAt);
  const end = parseTime(voucher.endsAt);
  if (start !== null && t < start) return false;
  if (end !== null && t > end) return false;
  return true;
}

/** Badge status untuk daftar admin (Kedaluwarsa & Habis diturunkan). */
export function voucherEffectiveStatus(
  voucher: Pick<Voucher, "status" | "startsAt" | "endsAt" | "quotaTotal">,
  now: Date,
  usedCount = 0
): VoucherEffectiveStatus {
  if (voucher.status === "draft") return "Draft";
  if (voucher.status === "inactive") return "Nonaktif";
  const t = now.getTime();
  const start = parseTime(voucher.startsAt);
  const end = parseTime(voucher.endsAt);
  if (end !== null && t > end) return "Kedaluwarsa";
  if (start !== null && t < start) return "Terjadwal";
  if (voucher.quotaTotal !== null && usedCount >= voucher.quotaTotal) return "Habis";
  return "Aktif";
}

/**
 * Berhakkah pembeli ber-level `slot` (null = tanpa level = slot 0)?
 * Kosong = semua pembeli — cermin `promoAllowsLevel` tapi dengan slot 0 eksplisit.
 */
export function voucherAllowsLevel(
  voucher: Pick<Voucher, "levelSlots">,
  slot: number | null | undefined
): boolean {
  const slots = voucher.levelSlots ?? [];
  if (!slots.length) return true;
  const key = slot === null || slot === undefined ? 0 : slot;
  return slots.includes(key);
}

export type VoucherProductRef = {
  id: string;
  brand?: string | null;
  category?: string | null;
  productType?: "preorder" | "ready_stock" | null;
};

/** Cakupan tipe + target (produk by id; kategori & merek BY NAMA) — cermin `campaignCoversProduct`. */
export function voucherCoversProduct(
  voucher: Pick<Voucher, "productTypeScope" | "targetType" | "targets">,
  product: VoucherProductRef
): boolean {
  if (voucher.productTypeScope !== "all") {
    const type = product.productType ?? "preorder";
    if (type !== voucher.productTypeScope) return false;
  }
  if (voucher.targetType === "all") return true;
  const brand = normName(product.brand);
  const category = normName(product.category);
  for (const target of voucher.targets) {
    if (target.kind !== voucher.targetType) continue;
    if (target.kind === "product") {
      if (target.productId && target.productId === product.id) return true;
      continue;
    }
    const name = normName(target.name);
    if (!name) continue;
    if (target.kind === "brand" && name === brand) return true;
    if (target.kind === "category" && name === category) return true;
  }
  return false;
}

/** Ekspedisi tercakup voucher ongkir (nama, lower+trim). */
export function voucherCoversExpedition(
  voucher: Pick<Voucher, "expeditionScope" | "expeditions">,
  expedition: string | null | undefined
): boolean {
  if (voucher.expeditionScope === "all") return true;
  const name = normName(expedition);
  if (!name) return false;
  return voucher.expeditions.some((e) => normName(e.name) === name);
}

export type VoucherBlockReason =
  | "kind"
  | "inactive"
  | "not_started"
  | "expired"
  | "quota"
  | "per_customer"
  | "level"
  | "no_eligible_lines"
  | "min_purchase"
  | "expedition"
  | "special_expedition"
  | "no_items"
  /** Voucher ber-kode tanpa kode / kode tak cocok lagi (mig 6990). */
  | "code";

/** Kalimat alasan untuk layar pembeli. */
/**
 * Siapa yang membaca pesannya. Layar admin memasang voucher ATAS NAMA reseller,
 * jadi kalimat bersuara pembeli ("akun Anda", "keranjang Anda") salah rujukan
 * orang di sana — batas & levelnya milik PELANGGAN, bukan admin (audit
 * 2026-09-10).
 */
export type VoucherAudience = "buyer" | "admin";

export function voucherBlockMessage(
  reason: VoucherBlockReason,
  voucher: Pick<Voucher, "minPurchase" | "kind">,
  audience: VoucherAudience = "buyer"
): string {
  const admin = audience === "admin";
  switch (reason) {
    case "kind":
      return "Voucher ini bukan untuk transaksi ini.";
    case "inactive":
      return "Voucher sedang tidak aktif.";
    case "not_started":
      return "Voucher belum mulai berlaku.";
    case "expired":
      return "Voucher sudah melewati periode aktif.";
    case "quota":
      return "Kuota voucher sudah habis.";
    case "per_customer":
      return admin
        ? "Batas pemakaian voucher untuk pelanggan ini sudah tercapai."
        : "Kamu sudah mencapai batas pemakaian voucher ini.";
    case "level":
      return admin
        ? "Voucher ini tidak berlaku untuk level pelanggan ini."
        : "Voucher ini tidak berlaku untuk level akunmu.";
    case "no_eligible_lines":
      return admin
        ? "Tidak ada baris pesanan ini yang memenuhi syarat voucher tersebut."
        : "Tidak ada produk di keranjang yang memenuhi syarat voucher ini.";
    case "min_purchase":
      return `Minimal pembelian ${formatRp(voucher.minPurchase)} untuk produk yang memenuhi syarat.`;
    case "expedition":
      return "Voucher ini tidak berlaku untuk ekspedisi yang dipilih.";
    case "special_expedition":
      return "Voucher ongkir tidak berlaku untuk ambil di tempat / COD.";
    case "no_items":
      if (voucher.kind === "ongkir") return "Tidak ada barang di paket ini yang memenuhi syarat voucher.";
      return admin ? "Tidak ada baris pesanan yang bisa diberi voucher." : "Keranjang masih kosong.";
    case "code":
      return VOUCHER_CODE_STALE_MESSAGE;
  }
}

function formatRp(value: number): string {
  return `Rp ${new Intl.NumberFormat("id-ID").format(Math.max(0, Math.round(value)))}`;
}

/**
 * Konteks pemakaian yang dijawab SERVER (`list_my_voucher_candidates`):
 * hitungan pemakaian tak pernah ditebak klien.
 */
export type VoucherUsageContext = {
  usedTotal: number;
  usedByMe: number;
  /** Slot level pembeli; null = tanpa level. */
  levelSlot: number | null;
};

/** Pemeriksaan umum (status, periode, kuota, batas per pelanggan, level). */
export function voucherCommonBlock(
  voucher: Voucher,
  ctx: VoucherUsageContext,
  now: Date
): VoucherBlockReason | null {
  if (voucher.status !== "active") return "inactive";
  const t = now.getTime();
  const start = parseTime(voucher.startsAt);
  const end = parseTime(voucher.endsAt);
  if (start !== null && t < start) return "not_started";
  if (end !== null && t > end) return "expired";
  if (voucher.quotaTotal !== null && ctx.usedTotal >= voucher.quotaTotal) return "quota";
  if (voucher.perCustomerLimit !== null && ctx.usedByMe >= voucher.perCustomerLimit) {
    return "per_customer";
  }
  if (!voucherAllowsLevel(voucher, ctx.levelSlot)) return "level";
  return null;
}

// ─── Voucher produk: pratinjau keranjang ────────────────────────────────────

export type VoucherCartLine = {
  key: string;
  product: VoucherProductRef;
  qty: number;
  /** Harga jual umum; null = harga menyusul / belum pasti → tak layak voucher. */
  normalPrice: number | null;
  /** Harga PRA-voucher hasil `pricing.ts` (sudah promo/level); null = harga menyusul. */
  preVoucherUnitPrice: number | null;
  tier: PriceTier;
  /** Potongan KEDUA (promo harga coret + level); absen/null = satu tier. */
  tier2?: PriceTier;
  /** Baris berharga kampanye promo (jejak `promoCampaignId`). */
  promoCampaignId: string | null;
  /**
   * Harga tambahan varian (mig 7900) yang TERMASUK di `preVoucherUnitPrice` —
   * di luar basis voucher dan di luar tier: harga akhir = tier(dasar − alokasi)
   * + tambahan (cermin `voucher_evaluate_cart`). Absen/null = 0.
   */
  priceExtra?: number | null;
};

export type VoucherQuoteLine = {
  key: string;
  base: number;
  alloc: number;
  perPcs: number;
  unitPrice: number;
};

export type VoucherQuote =
  | {
      ok: true;
      /** Σ harga umum baris layak. */
      base: number;
      /** Basis setelah `maxPurchaseBase`. */
      discountable: number;
      amount: number;
      lines: VoucherQuoteLine[];
      /** Hemat nyata terhadap harga pra-voucher (Σ (P − harga baru) × qty). */
      savings: number;
    }
  | { ok: false; reason: VoucherBlockReason; message: string };

export type VoucherEligibilityOptions = {
  /**
   * Cakupan produk hasil SERVER (`list_my_voucher_candidates.covered_product_ids`).
   * Keranjang klien tidak memegang merek/kategori produk, jadi pencocokan target
   * BY NAMA hanya bisa dijawab DB; bila diberikan, ia MENGGANTIKAN
   * `voucherCoversProduct` (cakupan tipe sudah ikut dihitung di dalamnya).
   * Absen = cocokkan sendiri dari `line.product` (pemakai yang memegang datanya).
   */
  coveredProductIds?: ReadonlySet<string>;
};

/** Tambahan baris (mig 7900) sebagai angka ≥ 0; absen / tak sah = 0. */
export function linePriceExtra(raw: number | null | undefined): number {
  const v = Number(raw ?? 0);
  return Number.isFinite(v) && v > 0 ? Math.round(v) : 0;
}

/** Baris keranjang yang layak untuk voucher produk ini (tanpa cek periode/kuota). */
export function eligibleVoucherLines(
  voucher: Voucher,
  lines: readonly VoucherCartLine[],
  options: VoucherEligibilityOptions = {}
): VoucherCartLine[] {
  const covered = options.coveredProductIds;
  return lines.filter((l) => {
    if (l.qty <= 0) return false;
    if (l.normalPrice === null || !Number.isFinite(l.normalPrice) || l.normalPrice <= 0) return false;
    if (l.preVoucherUnitPrice === null) return false;
    if (!voucher.allowWithPromo && l.promoCampaignId) return false;
    return covered ? covered.has(l.product.id) : voucherCoversProduct(voucher, l.product);
  });
}

/**
 * Pratinjau voucher PRODUK atas keranjang — urutan cek: jenis → status/periode/
 * kuota/level → baris layak → minimum pembelian → nilai & alokasi → harga baru.
 */
export function quoteProductVoucher(
  voucher: Voucher,
  lines: readonly VoucherCartLine[],
  ctx: VoucherUsageContext,
  now: Date,
  options: VoucherEligibilityOptions = {}
): VoucherQuote {
  const fail = (reason: VoucherBlockReason): VoucherQuote => ({
    ok: false,
    reason,
    message: voucherBlockMessage(reason, voucher)
  });
  if (voucher.kind !== "product") return fail("kind");
  const common = voucherCommonBlock(voucher, ctx, now);
  if (common) return fail(common);
  if (!lines.length) return fail("no_items");
  const eligible = eligibleVoucherLines(voucher, lines, options);
  if (!eligible.length) return fail("no_eligible_lines");
  const base = eligible.reduce((s, l) => s + Math.floor(l.normalPrice as number) * l.qty, 0);
  if (base < voucher.minPurchase) return fail("min_purchase");
  const discountable = voucherDiscountableBase(voucher, base);
  const amount = voucherAmount(voucher, discountable);
  if (amount <= 0) return fail("min_purchase");
  const allocations = allocateVoucher(
    amount,
    eligible.map((l) => ({ key: l.key, base: Math.floor(l.normalPrice as number) * l.qty, qty: l.qty }))
  );
  const byKey = new Map(allocations.map((a) => [a.key, a] as const));
  let savings = 0;
  const out: VoucherQuoteLine[] = eligible.map((l) => {
    const a = byKey.get(l.key) ?? { key: l.key, alloc: 0, perPcs: 0 };
    // mig 7900: tambahan ditempel SESUDAH tier — sama dengan harga pra-voucher
    // yang sudah memuatnya, jadi `savings` tetap selisih yang sebenarnya.
    const unitPrice =
      unitPriceAfterVoucher(l.normalPrice as number, l.qty, a.alloc, l.tier, l.tier2 ?? null) +
      linePriceExtra(l.priceExtra);
    savings += Math.max(0, (l.preVoucherUnitPrice as number) - unitPrice) * l.qty;
    return {
      key: l.key,
      base: Math.floor(l.normalPrice as number) * l.qty,
      alloc: a.alloc,
      perPcs: a.perPcs,
      unitPrice
    };
  });
  return { ok: true, base, discountable, amount, lines: out, savings };
}

// ─── Voucher ongkir: pratinjau Request Kirim ────────────────────────────────

export type VoucherShipmentContext = {
  /** Nama ekspedisi yang dipilih; null = belum dipilih. */
  expedition: string | null;
  /** Ekspedisi khusus (Ambil di Tempat / COD) — ditentukan TS, bukan SQL. */
  isSpecialExpedition: boolean;
  /** Ongkir bruto (kutipan); null = ditentukan admin nanti. */
  grossOngkir: number | null;
  /** Nilai barang dalam paket yang tipenya masuk cakupan (Σ unit_price × pcs). */
  coveredItemsValue: number;
  /** Ada item paket yang tipenya masuk cakupan. */
  hasCoveredItems: boolean;
};

export type OngkirVoucherQuote =
  | {
      ok: true;
      /** Potongan atas ongkir bruto; null = menunggu ongkir ditetapkan admin. */
      amount: number | null;
      netOngkir: number | null;
    }
  | { ok: false; reason: VoucherBlockReason; message: string };

export function quoteOngkirVoucher(
  voucher: Voucher,
  ship: VoucherShipmentContext,
  ctx: VoucherUsageContext,
  now: Date
): OngkirVoucherQuote {
  const fail = (reason: VoucherBlockReason): OngkirVoucherQuote => ({
    ok: false,
    reason,
    message: voucherBlockMessage(reason, voucher)
  });
  if (voucher.kind !== "ongkir") return fail("kind");
  const common = voucherCommonBlock(voucher, ctx, now);
  if (common) return fail(common);
  if (ship.isSpecialExpedition) return fail("special_expedition");
  if (!voucherCoversExpedition(voucher, ship.expedition)) return fail("expedition");
  if (!ship.hasCoveredItems) return fail("no_items");
  if (ship.coveredItemsValue < voucher.minPurchase) return fail("min_purchase");
  if (ship.grossOngkir === null || !Number.isFinite(ship.grossOngkir)) {
    return { ok: true, amount: null, netOngkir: null };
  }
  const gross = Math.max(0, Math.floor(ship.grossOngkir));
  const amount = Math.min(gross, voucherAmount(voucher, gross));
  return { ok: true, amount, netOngkir: gross - amount };
}

/** Nilai voucher ongkir dari ongkir bruto (cermin `sync_voucher_ongkir_line`). */
export function ongkirVoucherAmount(terms: VoucherTerms, gross: number): number {
  const g = Math.max(0, Math.floor(Number.isFinite(gross) ? gross : 0));
  return Math.min(g, voucherAmount(terms, g));
}

// ─── Label & deskripsi ──────────────────────────────────────────────────────

/** 30 → "30", 12.5 → "12,5". */
export function formatVoucherValue(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

/** "90% (maks Rp 20.000)" / "Rp 20.000". */
export function describeVoucherDiscount(
  terms: Pick<VoucherTerms, "mode" | "value" | "maxDiscount">
): string {
  if (terms.mode === "nominal") return formatRp(terms.value);
  const cap =
    terms.maxDiscount !== null && Number.isFinite(terms.maxDiscount)
      ? ` (maks ${formatRp(terms.maxDiscount)})`
      : "";
  return `${formatVoucherValue(terms.value)}%${cap}`;
}

/** Ringkasan kartu admin, mis. "Voucher Produk · 10% (maks Rp 20.000) · 3 merek · Pre-Order". */
export function describeVoucher(voucher: Voucher): string {
  const jenis = voucher.kind === "ongkir" ? "Voucher Ongkir" : "Voucher Produk";
  const cakupan =
    voucher.kind === "ongkir"
      ? voucher.expeditionScope === "all"
        ? "semua ekspedisi"
        : `${voucher.expeditions.length} ekspedisi`
      : voucher.targetType === "all"
        ? "semua produk"
        : voucher.targetType === "product"
          ? `${voucher.targets.length} produk`
          : voucher.targetType === "brand"
            ? `${voucher.targets.length} merek`
            : `${voucher.targets.length} kategori`;
  const tipe =
    voucher.productTypeScope === "preorder"
      ? " · Pre-Order"
      : voucher.productTypeScope === "ready_stock"
        ? " · Ready Stock"
        : "";
  return `${jenis} · ${describeVoucherDiscount(voucher)} · ${cakupan}${tipe}`;
}

// ─── Kode voucher (mig 6990) ────────────────────────────────────────────────
//
// Keputusan pemilik 2026-10-07: voucher BER-KODE tersembunyi dari daftar
// pembeli; pembeli mengetik kodenya, dan kode itu berlaku untuk CHECKOUT SAAT
// ITU — tidak disimpan ke akun, jadi ia ikut setiap panggilan pratinjau &
// checkout (`p_code`). Admin memasang tanpa kode. Kode yang tak bisa dipakai
// akun ini (salah, Draft, belum mulai, kedaluwarsa, level, kuota, batas per
// pelanggan) dijawab server dengan satu alasan: `not_found`.

/** "Tampil di daftar" (pilih-langsung) atau "Pakai kode" (tersembunyi). */
export type VoucherAccessMode = "list" | "code";

/** Cermin CHECK `vouchers_code_format` (mig 6990). */
export const VOUCHER_CODE_MIN_LENGTH = 4;
export const VOUCHER_CODE_MAX_LENGTH = 20;
const VOUCHER_CODE_PATTERN = /^[A-Z0-9]{4,20}$/;

export const VOUCHER_CODE_NOT_FOUND_MESSAGE = "Kode tidak ditemukan.";
/** Pilihan ber-kode yang ditolak saat pratinjau/checkout (kode diganti admin, atau tak terbawa). */
export const VOUCHER_CODE_STALE_MESSAGE = "Kode voucher tidak ditemukan — masukkan ulang kodenya.";

/**
 * Alasan pratinjau yang MENAHAN pilihan voucher, bukan melepasnya (bughunt
 * D11-1; putusan "Lanjut" atas opsi (a), 2026-10-08).
 *
 * `rate_limited` (mig 7000): batas salah ketik diperiksa SEBELUM kode
 * dibandingkan, jadi selama pembeli terkunci kode yang BENAR pun dijawab
 * begitu. Melepas pilihannya membuang voucher yang sah — pembeli harus
 * mengetik ulang kodenya sesudah 15 menit, atau memesan tanpa potongan.
 * Pilihan dipertahankan; keranjang menahan checkout sampai pratinjau lolos
 * atau pembeli melepasnya sendiri.
 */
export function voucherPreviewHoldsPick(reason: string | null | undefined): boolean {
  return reason === "rate_limited";
}

/**
 * Cermin SQL `voucher_normalize_code`: buang semua selain A-Z/a-z/0-9 LALU
 * huruf besar (" hemat-10 " → "HEMAT10"); kosong → null. Urutannya sama
 * dengan SQL supaya huruf non-ASCII dibuang, bukan di-upper-kan jadi huruf lain.
 * Dipakai kolom kode editor admin DAN kolom kode pembeli.
 */
export function normalizeVoucherCode(raw: string | null | undefined): string | null {
  const out = (raw ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  return out ? out : null;
}

/** Kode tersimpan sah (sesudah normalisasi): 4–20 huruf besar/angka. */
export function isValidVoucherCode(code: string | null | undefined): boolean {
  return typeof code === "string" && VOUCHER_CODE_PATTERN.test(code);
}

/** Alfabet kode acak: tanpa 0/O dan 1/I/L — tak tertukar saat dibacakan lewat WA. */
const VOUCHER_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/**
 * Kode acak untuk tombol "Acak" di editor. `random` disuntik supaya tes
 * deterministik; layar memberi sumber dari `crypto.getRandomValues`. Keunikan
 * tetap ditegakkan server (unik per toko).
 */
export function generateVoucherCode(length = 8, random: () => number = Math.random): string {
  const n = Math.min(VOUCHER_CODE_MAX_LENGTH, Math.max(VOUCHER_CODE_MIN_LENGTH, Math.floor(length) || 8));
  let out = "";
  for (let i = 0; i < n; i += 1) {
    const r = random();
    const idx = Number.isFinite(r) ? Math.floor(Math.min(Math.max(r, 0), 0.999999) * VOUCHER_CODE_ALPHABET.length) : 0;
    out += VOUCHER_CODE_ALPHABET[idx];
  }
  return out;
}

export type VoucherCodeLookupFailure = "not_found" | "kind" | "rate_limited" | "login" | "error";

/** Hasil `find_my_voucher_by_code` → bentuk layar. */
export type VoucherCodeLookup =
  | { ok: true; candidate: VoucherCandidate }
  | { ok: false; reason: VoucherCodeLookupFailure; kind: VoucherKind | null; message: string };

/** Kalimat penolakan kode untuk pembeli. `foundKind` = jenis voucher pemilik kode (alasan `kind`). */
export function voucherCodeLookupMessage(
  reason: VoucherCodeLookupFailure,
  foundKind: VoucherKind | null = null
): string {
  switch (reason) {
    case "kind":
      if (foundKind === "ongkir") {
        return "Kode ini untuk Voucher Ongkir — masukkan saat Request Kirim atau Tagihan + Kirim.";
      }
      if (foundKind === "product") {
        return "Kode ini untuk Voucher Produk — masukkan di halaman Voucher keranjang.";
      }
      return VOUCHER_CODE_NOT_FOUND_MESSAGE;
    case "rate_limited":
      return "Terlalu banyak percobaan kode. Coba lagi dalam 15 menit.";
    case "login":
      return "Masuk (login) dulu untuk memakai kode voucher.";
    case "error":
      return "Kode belum bisa diperiksa — periksa koneksi lalu coba lagi.";
    case "not_found":
      return VOUCHER_CODE_NOT_FOUND_MESSAGE;
  }
}

const CODE_LOOKUP_FAILURES: readonly VoucherCodeLookupFailure[] = ["not_found", "kind", "rate_limited", "login"];

export function mapVoucherCodeLookup(raw: unknown): VoucherCodeLookup {
  const r = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (r?.ok === true) {
    const candidate = mapVoucherCandidateRow(r.voucher);
    if (candidate) {
      // Datang dari kode = ber-kode, apa pun isi kuncinya.
      return { ok: true, candidate: { ...candidate, voucher: { ...candidate.voucher, requiresCode: true } } };
    }
  }
  const reasonRaw = typeof r?.reason === "string" ? r.reason : "";
  const reason: VoucherCodeLookupFailure = (CODE_LOOKUP_FAILURES as readonly string[]).includes(reasonRaw)
    ? (reasonRaw as VoucherCodeLookupFailure)
    : "error";
  const kind: VoucherKind | null = r?.kind === "product" || r?.kind === "ongkir" ? r.kind : null;
  return { ok: false, reason, kind, message: voucherCodeLookupMessage(reason, kind) };
}

/** Sisa kuota; null = tak terbatas. */
export function voucherRemainingQuota(
  voucher: Pick<Voucher, "quotaTotal">,
  usedCount: number
): number | null {
  if (voucher.quotaTotal === null) return null;
  return Math.max(0, voucher.quotaTotal - Math.max(0, usedCount));
}

// ─── Mapper baris DB ────────────────────────────────────────────────────────

export type VoucherRow = {
  id: string;
  store_id?: string | null;
  kind?: string | null;
  name?: string | null;
  description?: string | null;
  status?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  discount_mode?: string | null;
  discount_value?: number | string | null;
  max_discount?: number | string | null;
  min_purchase?: number | string | null;
  max_purchase_base?: number | string | null;
  quota_total?: number | string | null;
  per_customer_limit?: number | string | null;
  level_slots?: Array<number | string> | null;
  product_type_scope?: string | null;
  target_type?: string | null;
  expedition_scope?: string | null;
  allow_with_promo?: boolean | null;
  code?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  voucher_targets?:
    | Array<{ kind?: string | null; product_id?: string | null; name?: string | null }>
    | null;
  voucher_expeditions?:
    | Array<{ expedition_id?: string | null; expedition_name?: string | null }>
    | null;
};

export function mapVoucherRow(row: VoucherRow): Voucher {
  const mode: VoucherDiscountMode = row.discount_mode === "nominal" ? "nominal" : "persen";
  const kind: VoucherKind = row.kind === "ongkir" ? "ongkir" : "product";
  const status: VoucherStatus =
    row.status === "active" ? "active" : row.status === "inactive" ? "inactive" : "draft";
  const scope: VoucherProductTypeScope =
    row.product_type_scope === "preorder" || row.product_type_scope === "ready_stock"
      ? row.product_type_scope
      : "all";
  const targetType: VoucherTargetType =
    row.target_type === "product" || row.target_type === "category" || row.target_type === "brand"
      ? row.target_type
      : "all";
  const targets: VoucherTarget[] = (row.voucher_targets ?? [])
    .map((t) => ({
      kind: (t.kind === "category" || t.kind === "brand" ? t.kind : "product") as VoucherTargetKind,
      productId: t.product_id ?? null,
      name: t.name ?? ""
    }))
    .filter((t) => (t.kind === "product" ? Boolean(t.productId) : Boolean(t.name.trim())));
  const expeditions: VoucherExpedition[] = (row.voucher_expeditions ?? [])
    .map((e) => ({ expeditionId: e.expedition_id ?? null, name: (e.expedition_name ?? "").trim() }))
    .filter((e) => Boolean(e.name));
  const quota = numOrNull(row.quota_total);
  const perCustomer = numOrNull(row.per_customer_limit);
  const maxDiscount = numOrNull(row.max_discount);
  const maxBase = numOrNull(row.max_purchase_base);
  const code = typeof row.code === "string" && row.code.trim() ? row.code.trim() : null;
  return {
    id: row.id,
    storeId: row.store_id ?? null,
    kind,
    name: row.name ?? "",
    description: row.description ?? null,
    status,
    startsAt: row.starts_at ?? null,
    endsAt: row.ends_at ?? null,
    mode,
    value: num(row.discount_value),
    maxDiscount: mode === "persen" ? maxDiscount : null,
    minPurchase: Math.max(0, num(row.min_purchase)),
    maxPurchaseBase: kind === "product" && maxBase !== null && maxBase > 0 ? maxBase : null,
    quotaTotal: quota !== null && quota > 0 ? Math.floor(quota) : null,
    perCustomerLimit: perCustomer !== null && perCustomer > 0 ? Math.floor(perCustomer) : null,
    // Plafon slot MENGIKUTI VOUCHER_LEVEL_SLOTS (0..15 sejak mig 7300; 0..8 sejak mig 5600 —
    // `grup_pelanggan_8_slot`; PROD sudah punya grup di slot 5). Versi lama
    // memangkas ke ≤ 4, jadi voucher khusus slot 5–8 dibaca sebagai `[]` =
    // "semua pembeli": tiketnya tampil bisa dipakai untuk level yang tak
    // berhak, dan sekali admin menekan Simpan pembatasannya HILANG dari DB.
    levelSlots: (row.level_slots ?? [])
      .map((v) => Number(v))
      .filter((n) => (VOUCHER_LEVEL_SLOTS as readonly number[]).includes(n)),
    productTypeScope: scope,
    targetType: kind === "ongkir" ? "all" : targetType,
    targets: kind === "ongkir" ? [] : targets,
    expeditionScope:
      kind === "ongkir" && row.expedition_scope === "selected" ? "selected" : "all",
    expeditions: kind === "ongkir" ? expeditions : [],
    allowWithPromo: row.allow_with_promo !== false,
    code,
    requiresCode: code !== null,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null
  };
}

/** Kolom `voucher_usages.terms` (snapshot) → syarat; kolom absen jatuh ke voucher hidup. */
export function mapVoucherTerms(raw: unknown): VoucherTerms | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const mode: VoucherDiscountMode = r.mode === "nominal" ? "nominal" : "persen";
  return {
    mode,
    value: num(r.value),
    maxDiscount: mode === "persen" ? numOrNull(r.maxDiscount ?? r.max_discount) : null,
    minPurchase: Math.max(0, num(r.minPurchase ?? r.min_purchase)),
    maxPurchaseBase: numOrNull(r.maxPurchaseBase ?? r.max_purchase_base)
  };
}

// ─── Draf editor admin ──────────────────────────────────────────────────────

export type VoucherDraft = {
  id?: string | null;
  kind: VoucherKind;
  name: string;
  description: string;
  status: VoucherStatus;
  startsAt: string | null;
  endsAt: string | null;
  discountMode: VoucherDiscountMode;
  /** Mentah dari input (persen boleh koma desimal; nominal rupiah bulat). */
  discountValue: string;
  maxDiscount: string;
  minPurchase: string;
  maxPurchaseBase: string;
  quotaTotal: string;
  perCustomerLimit: string;
  levelSlots: number[];
  productTypeScope: VoucherProductTypeScope;
  targetType: VoucherTargetType;
  targets: VoucherTarget[];
  expeditionScope: VoucherExpeditionScope;
  expeditions: VoucherExpedition[];
  allowWithPromo: boolean;
  /** Cara pakai pembeli (mig 6990): pilih dari daftar, atau ketik kode. */
  accessMode: VoucherAccessMode;
  /** Mentah dari input; dinormalisasi saat validasi & kirim. Diabaikan pada mode "list". */
  code: string;
};

/** Angka dari input, sadar-mode (cermin `parsePromoValue`). */
export function parseVoucherValue(raw: string, mode: VoucherDiscountMode): number | null {
  if (mode === "nominal") {
    const digits = (raw ?? "").replace(/[^0-9]/g, "");
    if (!digits) return null;
    const n = Number.parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
  }
  const t = (raw ?? "").trim().replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Rupiah bulat dari input; kosong → null; tak terbaca → NaN-safe null. */
export function parseVoucherRupiah(raw: string): number | null {
  const digits = (raw ?? "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number.parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
}

/** Bilangan bulat positif dari input; kosong → null (= tak terbatas). */
export function parseVoucherCount(raw: string): number | null {
  const digits = (raw ?? "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number.parseInt(digits, 10);
  return Number.isFinite(n) ? n : null;
}

export function emptyVoucherDraft(kind: VoucherKind = "product"): VoucherDraft {
  return {
    id: null,
    kind,
    name: "",
    description: "",
    status: "draft",
    startsAt: null,
    endsAt: null,
    discountMode: "persen",
    discountValue: "",
    maxDiscount: "",
    minPurchase: "",
    maxPurchaseBase: "",
    quotaTotal: "",
    perCustomerLimit: "",
    levelSlots: [],
    productTypeScope: "all",
    targetType: "all",
    targets: [],
    expeditionScope: "all",
    expeditions: [],
    allowWithPromo: true,
    accessMode: "list",
    code: ""
  };
}

function rpText(value: number | null): string {
  return value === null ? "" : String(Math.round(value));
}

export function draftFromVoucher(voucher: Voucher): VoucherDraft {
  return {
    id: voucher.id,
    kind: voucher.kind,
    name: voucher.name,
    description: voucher.description ?? "",
    status: voucher.status,
    startsAt: voucher.startsAt,
    endsAt: voucher.endsAt,
    discountMode: voucher.mode,
    discountValue:
      voucher.mode === "persen" ? formatVoucherValue(voucher.value) : rpText(voucher.value),
    maxDiscount: rpText(voucher.maxDiscount),
    minPurchase: voucher.minPurchase > 0 ? rpText(voucher.minPurchase) : "",
    maxPurchaseBase: rpText(voucher.maxPurchaseBase),
    quotaTotal: voucher.quotaTotal === null ? "" : String(voucher.quotaTotal),
    perCustomerLimit: voucher.perCustomerLimit === null ? "" : String(voucher.perCustomerLimit),
    levelSlots: voucher.levelSlots,
    productTypeScope: voucher.productTypeScope,
    targetType: voucher.targetType,
    targets: voucher.targets,
    expeditionScope: voucher.expeditionScope,
    expeditions: voucher.expeditions,
    allowWithPromo: voucher.allowWithPromo,
    accessMode: voucher.code ? "code" : "list",
    code: voucher.code ?? ""
  };
}

/** Validasi draf — cermin CHECK tabel `vouchers`; `null` = lolos. */
export function validateVoucherDraft(draft: VoucherDraft): string | null {
  if (!draft.name.trim()) return "Nama voucher wajib diisi.";

  if (draft.accessMode === "code") {
    const code = normalizeVoucherCode(draft.code);
    if (!code) return "Kode voucher wajib diisi, atau pilih Tampil di Daftar.";
    if (!isValidVoucherCode(code)) {
      return `Kode voucher harus ${VOUCHER_CODE_MIN_LENGTH}–${VOUCHER_CODE_MAX_LENGTH} huruf/angka (spasi & tanda baca dibuang otomatis).`;
    }
  }

  const value = parseVoucherValue(draft.discountValue, draft.discountMode);
  if (value === null) return "Besar potongan wajib diisi angka.";
  if (value <= 0) return "Besar potongan harus lebih dari 0.";
  if (draft.discountMode === "persen") {
    if (value > 100) return "Potongan persen tidak boleh lebih dari 100%.";
    // Plafon OPSIONAL (mig 5660): kosong = potongan persis N % tanpa plafon;
    // diisi harus > 0 (cermin `admin_upsert_voucher`).
    if (draft.maxDiscount.trim()) {
      const cap = parseVoucherRupiah(draft.maxDiscount);
      if (cap === null || cap <= 0) {
        return "Maksimal potongan harus lebih dari 0, atau kosongkan (potongan persis % yang diisi).";
      }
    }
  }

  const minPurchase = parseVoucherRupiah(draft.minPurchase);
  if (draft.minPurchase.trim() && minPurchase === null) return "Minimum pembelian tidak terbaca.";

  if (draft.kind === "product") {
    const maxBase = parseVoucherRupiah(draft.maxPurchaseBase);
    if (draft.maxPurchaseBase.trim() && (maxBase === null || maxBase <= 0)) {
      return "Batas nilai pembelian yang didiskon harus lebih dari 0, atau kosongkan.";
    }
    if (maxBase !== null && minPurchase !== null && maxBase < minPurchase) {
      return "Batas nilai pembelian yang didiskon tidak boleh lebih kecil dari minimum pembelian.";
    }
    if (draft.targetType !== "all" && draft.targets.length === 0) {
      return "Pilih minimal satu target, atau ganti cakupan ke Semua Produk.";
    }
  } else if (draft.expeditionScope === "selected" && draft.expeditions.length === 0) {
    return "Pilih minimal satu ekspedisi, atau ganti cakupan ke Semua Ekspedisi.";
  }

  const quota = parseVoucherCount(draft.quotaTotal);
  if (draft.quotaTotal.trim() && (quota === null || quota <= 0)) {
    return "Total kuota harus bilangan bulat lebih dari 0, atau kosongkan (tak terbatas).";
  }
  const perCustomer = parseVoucherCount(draft.perCustomerLimit);
  if (draft.perCustomerLimit.trim() && (perCustomer === null || perCustomer <= 0)) {
    return "Batas per pelanggan harus bilangan bulat lebih dari 0, atau kosongkan (tak terbatas).";
  }
  if (quota !== null && perCustomer !== null && perCustomer > quota) {
    return "Batas per pelanggan tidak boleh melebihi total kuota.";
  }

  const start = parseTime(draft.startsAt);
  const end = parseTime(draft.endsAt);
  if (start !== null && end !== null && end <= start) {
    return "Tanggal berakhir harus sesudah tanggal mulai.";
  }

  if (draft.levelSlots.some((s) => !(VOUCHER_LEVEL_SLOTS as readonly number[]).includes(s))) {
    return "Pilihan grup pelanggan tidak valid — muat ulang halaman lalu pilih ulang.";
  }

  return null;
}

/** Draf → argumen RPC `admin_upsert_voucher`. */
export function voucherDraftToPayload(draft: VoucherDraft): {
  voucher: Record<string, unknown>;
  targets: Array<Record<string, unknown>>;
  expeditions: Array<Record<string, unknown>>;
} {
  const isProduct = draft.kind === "product";
  const value = parseVoucherValue(draft.discountValue, draft.discountMode) ?? 0;
  return {
    voucher: {
      id: draft.id ?? null,
      kind: draft.kind,
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      status: draft.status,
      startsAt: draft.startsAt || null,
      endsAt: draft.endsAt || null,
      discountMode: draft.discountMode,
      discountValue: value,
      maxDiscount:
        draft.discountMode === "persen" && draft.maxDiscount.trim() ? parseVoucherRupiah(draft.maxDiscount) : null,
      minPurchase: parseVoucherRupiah(draft.minPurchase) ?? 0,
      maxPurchaseBase: isProduct ? parseVoucherRupiah(draft.maxPurchaseBase) : null,
      quotaTotal: parseVoucherCount(draft.quotaTotal),
      perCustomerLimit: parseVoucherCount(draft.perCustomerLimit),
      // SELALU dikirim (idiom promo): kunci absen = "jangan sentuh" di RPC.
      // Plafon slot mengikuti VOUCHER_LEVEL_SLOTS (0..15 sejak mig 7300) — dulu
      // dipatok ≤ 4 sehingga target grup slot 5–8 hilang senyap saat disimpan.
      levelSlots: [...new Set(draft.levelSlots)]
        .filter((s) => (VOUCHER_LEVEL_SLOTS as readonly number[]).includes(s))
        .sort((a, b) => a - b),
      productTypeScope: draft.productTypeScope,
      targetType: isProduct ? draft.targetType : "all",
      expeditionScope: isProduct ? "all" : draft.expeditionScope,
      allowWithPromo: isProduct ? draft.allowWithPromo : true,
      // SELALU dikirim (mig 6990): null = "Tampil di daftar". Kunci ABSEN = jangan
      // sentuh — itu hanya untuk bundel klien lama yang belum mengenal kode.
      code: draft.accessMode === "code" ? normalizeVoucherCode(draft.code) : null
    },
    targets:
      !isProduct || draft.targetType === "all"
        ? []
        : draft.targets
            .filter((t) => t.kind === draft.targetType)
            .map((t) => ({ kind: t.kind, productId: t.productId, name: t.name })),
    expeditions:
      isProduct || draft.expeditionScope === "all"
        ? []
        : draft.expeditions.map((e) => ({ expeditionId: e.expeditionId, name: e.name }))
  };
}

// ─── Jejak di baris pesanan / paket ─────────────────────────────────────────

/** Label pil "Voucher: <nama>" untuk baris ber-jejak; null bila tak ada jejak. */
export function voucherTraceLabel(name: string | null | undefined): string | null {
  const n = (name ?? "").trim();
  return n ? `Voucher ${n}` : null;
}

/**
 * Pesan checkout dari kode galat RPC `VOUCHER_INVALID:<sebab>` — dipetakan ke
 * kalimat yang sama dengan pratinjau supaya pembeli membaca satu bahasa.
 */
export function voucherErrorMessage(raw: string | null | undefined, fallback: string): string {
  const m = /VOUCHER_INVALID:([a-z_]+)/.exec(raw ?? "");
  if (!m) return fallback;
  const reason = m[1];
  if (reason === "price_changed" || reason === "inconsistent") {
    return "Harga produk berubah sejak keranjang dimuat — muat ulang keranjang lalu coba lagi.";
  }
  if (reason === "min_purchase") return "Minimum pembelian voucher tidak terpenuhi.";
  // mig 5680 — gerbang otoritas harga baris pada `admin_set_order_voucher`.
  if (reason === "line_price_changed") {
    return "Harga baris pesanan tidak sama dengan yang tampil di layar ini — muat ulang halaman lalu coba lagi.";
  }
  if (reason === "line_price_unknown") {
    return "Ada baris pesanan yang harganya belum bisa dipastikan — muat ulang halaman lalu coba lagi.";
  }
  // Nota + Kirim admin (mig 5650): voucher ongkir tanpa nominal ongkir tak punya
  // apa-apa untuk dipotong — server menolak, bukan diam-diam memakai kuota.
  if (reason === "no_ongkir") return "Voucher ongkir butuh nominal ongkir — isi ongkirnya dulu.";
  // mig 6990: voucher ber-kode tanpa kode / kode tak cocok lagi (diganti admin).
  if (reason === "code") return VOUCHER_CODE_STALE_MESSAGE;
  // mig 7000: kode salah di pratinjau ikut dihitung batas salah ketik.
  if (reason === "rate_limited") return voucherCodeLookupMessage("rate_limited");
  const known: VoucherBlockReason[] = [
    "kind", "inactive", "not_started", "expired", "quota", "per_customer",
    "level", "no_eligible_lines", "expedition", "special_expedition", "no_items"
  ];
  if ((known as string[]).includes(reason)) {
    return voucherBlockMessage(reason as VoucherBlockReason, { minPurchase: 0, kind: "product" });
  }
  return fallback;
}

/**
 * Pesan gagal SIMPAN voucher (editor admin). Pemeriksaan duplikat kode di
 * `admin_upsert_voucher` tak dikunci: dua admin yang menyimpan kode yang sama
 * pada saat bersamaan sama-sama lolos cek, dan yang kedua baru ditolak indeks
 * unik `vouchers_store_code_uidx` — galat mentah Postgres itu diterjemahkan di
 * sini (bughunt D10-8). Pesan lain (mis. kalimat RPC) diteruskan apa adanya.
 */
export function voucherSaveErrorMessage(raw: string | null | undefined, fallback = "Gagal menyimpan."): string {
  const msg = (raw ?? "").trim();
  if (!msg) return fallback;
  if (/vouchers_store_code_uidx/.test(msg)) {
    return "Kode voucher itu baru saja dipakai voucher lain — kode unik per toko, pilih kode lain.";
  }
  if (/vouchers_code_format/.test(msg)) {
    return `Kode voucher harus ${VOUCHER_CODE_MIN_LENGTH}–${VOUCHER_CODE_MAX_LENGTH} huruf/angka (tanpa spasi atau tanda baca).`;
  }
  return msg;
}

// ─── Kandidat & pratinjau dari SERVER (RPC mig 5610) ────────────────────────

/** Satu baris `list_my_voucher_candidates`: voucher + jawaban server tentang pembeli ini. */
export type VoucherCandidate = {
  voucher: Voucher;
  usedTotal: number;
  usedByMe: number;
  levelOk: boolean;
  /** Produk (dari `p_product_ids`) yang tercakup target + tipe — dijawab DB. */
  coveredProductIds: string[];
  expeditionOk: boolean;
  /** Alasan terblokir versi server; null = boleh dicoba. */
  blockedReason: VoucherBlockReason | null;
};

const SERVER_BLOCK_REASONS: readonly VoucherBlockReason[] = ["quota", "per_customer", "level", "expedition"];

export function mapVoucherCandidateRow(raw: unknown): VoucherCandidate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !r.id) return null;
  const reason = typeof r.blocked_reason === "string" ? r.blocked_reason : null;
  const voucher = mapVoucherRow(r as unknown as VoucherRow);
  return {
    // Pembeli tak pernah menerima `code` (dibuang server, mig 6990) — tandanya
    // datang lewat `requires_code`.
    voucher: r.requires_code === true ? { ...voucher, requiresCode: true } : voucher,
    usedTotal: Math.max(0, Math.floor(num(r.used_total))),
    usedByMe: Math.max(0, Math.floor(num(r.used_by_me))),
    levelOk: r.level_ok !== false,
    coveredProductIds: Array.isArray(r.covered_product_ids)
      ? r.covered_product_ids.filter((x): x is string => typeof x === "string" && x !== "")
      : [],
    expeditionOk: r.expedition_ok !== false,
    blockedReason:
      reason && (SERVER_BLOCK_REASONS as readonly string[]).includes(reason)
        ? (reason as VoucherBlockReason)
        : null
  };
}

export function mapVoucherCandidateRows(raw: unknown): VoucherCandidate[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(mapVoucherCandidateRow).filter((c): c is VoucherCandidate => c !== null);
}

/**
 * Baris keranjang untuk RPC `preview_my_voucher` / `checkout_orders_with_voucher`
 * — kolom snake_case persis kontrak `voucher_evaluate_cart` (mig 5610).
 */
export type VoucherRpcLine = {
  key: string;
  productId: string;
  variantId: string | null;
  variantName: string | null;
  qty: number;
  /** Harga jual umum yang dilihat klien; server memverifikasi ke DB. */
  normalPrice: number | null;
  /** Harga PRA-voucher hasil `pricing.ts`. */
  unitPrice: number | null;
  tier: PriceTier;
  /** Potongan KEDUA (promo harga coret + level) — `tier2_mode/tier2_value`; absen = satu tier. */
  tier2?: PriceTier;
  /**
   * Seluruh potongan yang membentuk `unitPrice` (jejak `order_items.promo_/
   * level_discount_*`, mig promo_harga_coret). Absen = tak tercatat.
   */
  cuts?: readonly PriceCut[];
  promoCampaignId: string | null;
  promoCampaignName?: string | null;
  note?: string | null;
  /**
   * Tambahan varian (mig 7900) yang TERMASUK di `unitPrice`. Mesin voucher tak
   * pernah memakainya (server membaca DB; `checkout_orders_with_voucher` membuang
   * kunci ini dari masukan mesin) — bahan layar: pratinjau voucher, "Harga Umum"
   * ringkasan, pencocokan harga nota. Muatan CHECKOUT (`voucherCheckoutLinePayload`)
   * mengirimnya untuk dipotret pada baris yang TAK layak voucher (mig 8080).
   */
  priceExtra?: number | null;
};

/**
 * Kunci jejak potongan untuk muatan baris `order_items` — SATU pembuat untuk
 * checkout biasa (`data.ts`), checkout ber-voucher, dan keranjang admin, supaya
 * keempat kolom tak pernah diisi dengan cara berbeda. Potongan promo = `cuts`
 * ber-`source "promo"`; potongan level = `override` | `group`. Tanpa `cuts`
 * (harga menyusul / jalur fallback) keempatnya null = "tak tercatat".
 */
export function discountTracePayload(
  cuts: readonly PriceCut[] | null | undefined
): {
  promo_discount_mode: PriceCut["mode"] | null;
  promo_discount_value: number | null;
  level_discount_mode: PriceCut["mode"] | null;
  level_discount_value: number | null;
} {
  const promo = cuts?.find((c) => c.source === "promo") ?? null;
  // isLevelCut (promo.ts): diskon merek per level (mig 7070) ikut sisi level.
  const level = cuts?.find((c) => isLevelCut(c)) ?? null;
  return {
    promo_discount_mode: promo?.mode ?? null,
    promo_discount_value: promo?.value ?? null,
    level_discount_mode: level?.mode ?? null,
    level_discount_value: level?.value ?? null
  };
}

/**
 * Muatan satu baris keranjang untuk RPC CHECKOUT (`checkout_orders_with_voucher`,
 * `checkout_orders_with_bundles`): `voucherRpcLinePayload` + tambahan yang
 * DIKETAHUI (`price_extra`) — cermin checkout tanpa voucher yang menulis
 * `unit_price_extra` eksplisit (grant 8020). Server memotretnya untuk baris yang
 * TAK layak voucher (harganya pun dari klien; mig 8070 (1) bundel, mig 8080
 * bughunt D15 F1 voucher); baris layak memakai keluaran mesin. Harga / tambahan
 * tak diketahui → kunci tak dikirim (NULL → trigger, D14-9). Dijepit 0 ≤
 * tambahan ≤ harga, sama dengan server. Bukan untuk pratinjau. Murni.
 */
export function voucherCheckoutLinePayload(line: VoucherRpcLine): Record<string, unknown> {
  const payload = voucherRpcLinePayload(line);
  const unit = line.unitPrice;
  const extra = line.priceExtra;
  if (typeof unit !== "number" || !Number.isFinite(unit)) return payload;
  if (typeof extra !== "number" || !Number.isFinite(extra)) return payload;
  return { ...payload, price_extra: Math.max(0, Math.min(Math.round(extra), Math.round(unit))) };
}

export function voucherRpcLinePayload(line: VoucherRpcLine): Record<string, unknown> {
  return {
    key: line.key,
    product_id: line.productId,
    variant_id: line.variantId,
    variant_name: line.variantName,
    qty: Math.max(0, Math.floor(line.qty)),
    normal_price: line.normalPrice,
    unit_price: line.unitPrice,
    tier_mode: line.tier?.mode ?? null,
    tier_value: line.tier?.value ?? null,
    tier2_mode: line.tier2?.mode ?? null,
    tier2_value: line.tier2?.value ?? null,
    ...discountTracePayload(line.cuts),
    promo_campaign_id: line.promoCampaignId,
    promo_campaign_name: line.promoCampaignName ?? null,
    note: line.note ?? null
  };
}

export type VoucherServerPreviewLine = {
  key: string;
  eligible: boolean;
  normalPrice: number | null;
  alloc: number;
  perPcs: number;
  /** Harga satuan SETELAH voucher (yang akan ditulis server). */
  unitPrice: number | null;
  /** Tambahan baris menurut server (mig 7900), sudah termasuk di `unitPrice`; absen pada DB lama. */
  priceExtra?: number | null;
};

export type VoucherServerPreviewFailure =
  | VoucherBlockReason
  | "price_changed"
  | "inconsistent"
  /** Batas salah ketik kode tercapai (10 / 15 menit, mig 7000 — gerbang pratinjau ikut mencatat). */
  | "rate_limited"
  | "login"
  | "error";

export type VoucherServerPreview =
  | {
      ok: true;
      voucherId: string;
      voucherName: string;
      base: number;
      discountable: number;
      amount: number;
      lines: VoucherServerPreviewLine[];
    }
  | { ok: false; reason: VoucherServerPreviewFailure; message: string };

/** Keluaran `preview_my_voucher` → bentuk layar; pesan memakai kosakata yang sama dengan pratinjau TS. */
export function mapVoucherPreview(raw: unknown): VoucherServerPreview {
  const r = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!r || r.ok !== true) {
    const reason = typeof r?.reason === "string" && r.reason ? r.reason : "error";
    if (reason === "login") {
      return { ok: false, reason, message: "Masuk (login) dulu untuk memakai voucher." };
    }
    const fallback =
      typeof r?.message === "string" && r.message ? r.message : "Voucher tidak bisa dipakai untuk keranjang ini.";
    const known: readonly string[] = [
      "kind", "inactive", "not_started", "expired", "quota", "per_customer", "level",
      "no_eligible_lines", "min_purchase", "expedition", "special_expedition", "no_items",
      "price_changed", "inconsistent", "code", "rate_limited"
    ];
    return {
      ok: false,
      reason: known.includes(reason) ? (reason as VoucherServerPreviewFailure) : "error",
      message: voucherErrorMessage(`VOUCHER_INVALID:${reason}`, fallback)
    };
  }
  const lines: VoucherServerPreviewLine[] = Array.isArray(r.lines)
    ? r.lines
        .filter((l): l is Record<string, unknown> => Boolean(l) && typeof l === "object")
        .map((l) => ({
          key: typeof l.key === "string" ? l.key : "",
          eligible: l.eligible === true,
          normalPrice: numOrNull(l.normal_price),
          alloc: Math.max(0, num(l.alloc)),
          perPcs: Math.max(0, num(l.per_pcs)),
          unitPrice: numOrNull(l.unit_price),
          priceExtra: numOrNull(l.price_extra)
        }))
    : [];
  return {
    ok: true,
    voucherId: typeof r.voucher_id === "string" ? r.voucher_id : "",
    voucherName: typeof r.voucher_name === "string" ? r.voucher_name : "",
    base: Math.max(0, num(r.base)),
    discountable: Math.max(0, num(r.discountable)),
    amount: Math.max(0, num(r.amount)),
    lines
  };
}

// ─── Voucher ONGKIR: isi paket & pratinjau server (RPC mig 5620/5650) ────────

/** Satu item paket untuk `preview_my_ongkir_voucher` / `admin_preview_ongkir_voucher`. */
export type OngkirVoucherItem = { orderItemId: string; qty: number };

export type OngkirVoucherServerPreview =
  | { ok: true; base: number; voucherName: string; amount: number | null; net: number | null }
  | { ok: false; reason: VoucherServerPreviewFailure; message: string };

/** `[{order_item_id, qty}]` — kontrak `voucher_ongkir_check`; qty dibulatkan ke bawah, ≥ 0. */
export function ongkirVoucherItemsPayload(
  items: readonly OngkirVoucherItem[]
): Array<{ order_item_id: string; qty: number }> {
  return items.map((i) => ({ order_item_id: i.orderItemId, qty: Math.max(0, Math.floor(i.qty)) }));
}

/** Ongkir bruto untuk `p_gross`: null = belum ada angka (potongan menyusul saat ongkir ditetapkan). */
export function ongkirVoucherGrossPayload(gross: number | null | undefined): number | null {
  return gross === null || gross === undefined || !Number.isFinite(gross) ? null : Math.max(0, Math.round(gross));
}

/**
 * Keluaran RPC pratinjau voucher ongkir (pembeli maupun admin — bentuknya
 * sama) → bentuk layar. `amount`/`net` null = tanpa ongkir untuk dipotong.
 */
export function mapOngkirVoucherPreview(raw: unknown): OngkirVoucherServerPreview {
  const r = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  if (r.ok !== true) {
    const mapped = mapVoucherPreview({ ok: false, reason: r.reason, message: r.message });
    return mapped.ok ? { ok: false, reason: "error", message: "Voucher tidak bisa dipakai." } : mapped;
  }
  const amount = r.amount === null || r.amount === undefined ? null : Number(r.amount);
  const net = r.net === null || r.net === undefined ? null : Number(r.net);
  return {
    ok: true,
    base: Number(r.base ?? 0) || 0,
    voucherName: typeof r.voucher_name === "string" ? r.voucher_name : "",
    amount: amount !== null && Number.isFinite(amount) ? amount : null,
    net: net !== null && Number.isFinite(net) ? net : null
  };
}
